<template>
    <div>
        <modal id="transactionModalWrapper" v-if="showModal" @close="handleClose" ref="modalRef">
            <template #header>
                <span v-if="action == 'add-to-portfolio'">Add {{ label }} Transaction to Portfolio</span>
                <span v-else>{{ ucword(action) }} {{ label }} Transaction</span>
            </template>

            <template #body>
                <div class="py-2">
                    <div id="transactionSelectors" class="flex flex-wrap mb-4" v-if="!transactionItem && !transactionSymbol">
                        <div class="w-full" v-if="section === 'futures' && quotes">
                            <div class="text-sm mb-1 font-bold">Select Contract:</div>
                            <div class="selector-wrapper">
                                <contract-selector
                                    v-model="symbol"
                                    :initial="quotes.symbol"
                                    :all-contracts="quotes.futures"
                                />
                            </div>
                        </div>

                        <div class="w-full" v-if="(section === 'forex' || section === 'crypto') && quotes">
                            <div class="text-sm mb-1 font-bold">Select Crossrate:</div>
                            <div class="selector-wrapper">
                                <forex-selector
                                    :symbols="quotes.forex || quotes.crypto"
                                    v-model="symbol"
                                />
                            </div>
                        </div>

                        <div class="w-full" v-if="section === 'future-spreads' && quotes">
                            <div class="text-sm mb-1 font-bold">Select Futures Spread:</div>
                            <div class="selector-wrapper">
                                <spread-selector
                                    :spreads="quotes.spreads"
                                    :all-contracts="quotes.futures"
                                    v-model="symbol"
                                />
                            </div>
                        </div>

                        <div class="w-full" v-if="section === 'future-options' && quotes">
                            <div class="text-sm mb-1 font-bold">Select Strike:</div>
                            <div class="selector-wrapper">
                                <option-selector-2
                                    v-model="symbol"
                                    :groups="quotes.options"
                                />
                            </div>
                        </div>

                        <div class="w-full" v-if="section === 'stock-etf'">
                            <div class="text-sm mb-1 font-bold">Enter a symbol:</div>
                            <div class="selector-wrapper">
                                <stock-search-selector v-model="symbol" />
                            </div>
                        </div>
                    </div>

                    <div v-if="lastPriceQuotes" class="flex flex-wrap">
                        <div class="w-full bg-tan-200 text-center py-2 border mb-2 border-gray-200">
                            <span v-if="!dataUpdating">
                                <div class="mb-1" v-if="transactionSymbol">
                                    <p class="text-md font-bold leading-none mb-1 text-blue-500">{{ lastPriceQuotes.contractFullName || lastPriceQuotes.symbolName }}</p>
                                </div>
                                <div class="mb-1" v-if="transactionItem">
                                    <p class="text-md font-bold leading-none mb-1 text-blue-500">{{ transactionItem.contractFullName || transactionItem.symbol }}</p>
                                </div>
                                <p v-if="errorSymbolQuotes">Expired</p>
                                <p v-else class="text-sm font-bold leading-none">Price {{ lastPriceQuotes.lastPrice }} as of {{ lastPriceQuotes.tradeTime }}</p>
                            </span>
                            <spinner class="-my-1" v-else></spinner>
                        </div>

                        <div class="w-full p-0 mb-3 bg-red-500 px-2 py-1 text-white text-xs text-center" v-if="formError">
                            {{ formError }}
                        </div>

                        <div class="w-full" v-if="transactionSymbol">
                            <label for="selectedPortfolio">
                                Portfolio
                            </label>
                            <select v-model="form.portfolio" class="form-control form-control-sm form-input-modal" id="selectedPortfolio">
                                <option :value="portfolio" v-for="portfolio in portfolioData">{{ portfolio.caption }}</option>
                            </select>
                        </div>

                        <div class="w-1/3 pr-2">
                            <label for="positionAction">
                                Buy/Sell: <i v-tooltip="{ content: 'Purchase of an option is a Buy position', theme: 'info' }" class="text-gray-400 cursor-pointer fas fa-question-circle"></i>
                                <!-- <span class="text-xs text-gray-600 leading-tight font-normal italic">(Purchase of an option is a Buy position)</span> -->
                            </label>
                            <select v-model="form.action" class="form-control form-control-sm form-input-modal" id="positionAction">
                                <option value="1">Buy</option>
                                <option value="-1">Sell</option>
                            </select>
                        </div>

                        <div class="w-1/3 pr-1">
                            <label for="quantity">
                                <sup>*</sup>Qty:
                            </label>
                            <input v-model="form.quantity" class="form-input-modal" :class="{'is-invalid': errors.quantity}" id="quantity" type="number" min="1" placeholder="1">
                        </div>

                        <div class="w-1/3 pl-1">
                            <label for="commission">
                                $ per Trade:
                            </label>
                            <input v-model="form.commission" class="form-input-modal" id="commission" type="number" placeholder="0">
                        </div>

                        <div class="w-1/2 pr-1">
                            <label for="entry_price">
                                <sup>*</sup>Entry Price:
                            </label>
                            <price-input v-model="form.entry_price" :symbol-type="lastPriceQuotes.symbolType" :base-code="lastPriceQuotes.baseCode || '2'" :default-value="getDefaultValue('entry_price')" :class="{'is-invalid': errors.entry_price == true}" id="entry_price" class="form-input-modal" :symbol="symbol"></price-input>
                        </div>
                        <div class="w-1/2 pl-1">
                            <label for="entry_date">
                                <sup>*</sup>Entry Date:
                            </label>
                            <input v-model="form.entry_date" class="form-input-modal text-base" :class="{'is-invalid': errors.entry_date}" id="entry_date" type="date" pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}">
                        </div>

                        <div class="w-1/2 pr-1">
                            <label for="exit_price">
                                Exit Price:
                            </label>
                            <price-input v-model="form.exit_price" :symbol-type="lastPriceQuotes.symbolType" :base-code="lastPriceQuotes.baseCode || '2'" :default-value="getDefaultValue('exit_price')" :class="{'is-invalid': errors.exit_price == true}" id="exit_price" class="form-input-modal" :symbol="symbol"></price-input>
                        </div>

                        <div class="w-1/2 pl-1">
                            <label for="exit_date">
                                Exit Date:
                            </label>
                            <input v-model="form.exit_date" class="form-input-modal text-base" :class="{'is-invalid': errors.exit_date}" id="exit_date" type="date" pattern="[0-9]{2}/[0-9]{2}/[0-9]{4}">
                        </div>

                        <div class="w-full mt-3">
                            <textarea v-model="form.notes" class="form-input-modal" id="notes" type="text" rows="3" placeholder="Enter notes..."></textarea>
                        </div>
                    </div>
                    <spinner v-else-if='showSpinner'></spinner>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="handleClose">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="save">
                        <span v-if="action == 'add-to-portfolio'">Add to Portfolio</span>
                        <span v-else-if="action == 'edit'">Save</span>
                        <span v-else>{{ ucword(action) }}</span>
                    </div>
                </div>
            </template>
        </modal>
        <flash-message ref="transactionFlashRef" :seconds-to-display="6">
            <template #body>
                <div v-if="flashError">
                    Error adding to Trade Tracker
                </div>
                <div class="text-center" v-else>
                    <div>
                        Success! {{ transactionSymbol }} has been added to your Trade Tracker
                    </div>

                    <div>
                        <a class="text-xs" :href="getLink()">
                            Go to Trade Tracker
                        </a>
                    </div>
                </div>
            </template>
        </flash-message>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import PriceInput from '../../PriceInput';

const props = defineProps({
    label: {
        type: String,
        required: true
    },
    section: {
        type: String,
        // required: true
    },
    portfolioId: Number,
    portfolioAdjustCash: Number,
    transactionItem: Object,
    transactionSymbol: String,
    transactionOrderData: Object,
    action: String,
});

const emit = defineEmits(['close', 'updatedTransaction']);

const transactionFlashRef = ref(null);
const symbol = ref(null);

// For the sections select
const quotes = ref(null);
const portfolioData = ref(null);

const lastPriceQuotes = ref(null);
const dataUpdating = ref(false);
const showSpinner = ref(true);
const showModal = ref(true);

const form = ref({
    portfolioId: (props.action == 'add-to-portfolio') ? null : props.portfolioId,
    actionType: 'add',
    action: 1,
    quantity: 1,
    entry_date: new Date().toISOString().slice(0, 10),
    entry_price: null,
    exit_date: null,
    exit_price: null,
    commission: null,
    notes: null,
    portfolio: null,
    adjust_cash: false,
});

const formError = ref(false);
const errors = ref({
    'entry_date' : null,
    'entry_price' : null,
    'exit_date' : null,
    'exit_price' : null,
    'quantity': null
});
const flashError = ref(false);
const errorSymbolQuotes = ref(false);

const handleClose = () => {
    emit('close');
};

const ucword = (text) => {
    return text.charAt(0).toUpperCase() + text.slice(1)
};

const getLink = () => {
    return window.route('my-folder.trade-tracker.index');
};

const getDefaultValue = (field) => {
    return props.transactionItem && props.transactionItem.raw[field] || '';
};

const validate = () => {
    let valid = true;
    let validRequired = ['entry_date', 'entry_price', 'quantity', 'exit_date', 'exit_price'];
    validRequired.map((v) => {
        if (v == 'exit_date' || v == 'exit_price') {
            if (form.value['exit_date'] && form.value['exit_price'] !== null) {
                errors.value[v] = null;
            } else if (form.value['exit_date'] && form.value['exit_price'] !== null) {
                errors.value['exit_price'] = true;
                valid = false;
            } else {
                errors.value[v] = null;
            }
        } else if (!form.value[v]) {
            errors.value[v] = true;
            valid = false;
        } else {
            errors.value[v] = null;
        }

    });
    return valid;
};

const save = () => {
    if (!validate()) {
        formError.value = 'Please correct the errors highlighted below and try again';
        return;
    }

    form.value.actionType = props.action;
    form.value.portfolioId = (props.action == 'add-to-portfolio') ? form.value.portfolio.id : props.portfolioId;

    // create or update transaction depends on action
    if (props.action == 'add-to-portfolio') {
        form.value.adjust_cash = form.value.portfolio.adjust_cash || false;
        form.value.symbol = symbol.value;
        const route = window.route('api.trade-tracker.add-item');
        axios.post(route, form.value)
            .then((response) => {
                // show 'success' message
                flashError.value = false;
                transactionFlashRef.value.display();
                showModal.value = false;
                setTimeout(() => {
                    emit('close');
                }, 6000);
            }).catch((error) => {
                if (error && error.response.data.message) {
                    formError.value = error.response.data.message;
                } else {
                    formError.value = 'Error adding. Something went wrong, please try again later';
                }
                flashError.value = true;
                transactionFlashRef.value.error();
                console.error(error);
            });
    } else if (props.action == 'add') {
        form.value.adjust_cash = props.portfolioAdjustCash || false;
        form.value.symbol = symbol.value;
        const route = window.route('api.trade-tracker.add-item');
        axios.post(route, form.value)
            .then((response) => {
                emit('updatedTransaction');
            }).catch((error) => {
                if (error && error.response.data.message) {
                    formError.value = error.response.data.message;
                } else {
                    formError.value = 'Error adding. Something went wrong, please try again later';
                }
                console.error(error);
            });
    } else {
        form.value.id = props.transactionItem.id;
        const route = window.route('api.trade-tracker.update-item');
        axios.post(route, form.value)
            .then((response) => {
                emit('updatedTransaction');
            }).catch((error) => {
                if (error && error.response.data.message) {
                    formError.value = error.response.data.message;
                } else {
                    formError.value = 'Error updating. Something went wrong, please try again later';
                }
                console.error(error);
            });
    }
};

const getPortfolios = () => {
    const route = window.route('api.trade-tracker.portfolio-list');
    axios.get(route)
        .then((response) => {
            portfolioData.value = (response.data && response.data.data.length > 0) ? response.data.data : null;
            form.value.portfolio = portfolioData.value[0] || null;
            form.value.adjust_cash = form.value.portfolio.adjust_cash || false;
        }).catch((error) => {
            console.error(error);
        })
};

const getQuotesForSelects = () => {
    const route = window.route('api.quotes.symbol-select');
    const params = {
        section: props.section
    };
    axios.post(route, params)
        .then((response) => {
            quotes.value = response.data;
        }).catch((error) => {
            console.error('Could not retrieve quotes');
        })
};

const getLastPriceForSymbol = () => {
    dataUpdating.value = true;
    const route = window.route('api.quotes');
    axios.post(route, { after: 'guessBaseCode', symbol: symbol.value, fields: 'symbol,symbolType,symbolName,contractFullName,lastPrice,tradeTime,baseCode' })
        .then((response) => {
            if (response.data.errors && response.data.errors.invalidSymbols) {
                errorSymbolQuotes.value = true;

                lastPriceQuotes.value = {
                    baseCode: response.data.baseCode
                };
            } else {
                lastPriceQuotes.value = response.data.data[0];
            }
            dataUpdating.value = false;
        })
};

watch(() => symbol.value, (newVal) => {
    getLastPriceForSymbol();
});

onMounted(() => {
    // remove the spinner for 'Stocks & ETFs'
    if (props.section === 'stock-etf') {
        showSpinner.value = false;
    }

    // check action - 'add', 'add-to-portfolio (from DataTable)' or 'edit'
    if (props.action === 'add' || props.action === 'add-to-portfolio') {
        if (props.action === 'add-to-portfolio') {
            getPortfolios();
            symbol.value = props.transactionSymbol;
            form.value.symbol = props.transactionSymbol;

            // populate the data from 'Order Ticket' page
            if (props.transactionOrderData) {
                ['action', 'quantity', 'entry_date', 'entry_price', 'exit_date', 'exit_price', 'commission', 'notes'].forEach(key => {
                    if (key === 'action') {
                        form.value.action = props.transactionOrderData.long_short === 'S' ? '-1' : '1';
                    } else if (key === 'entry_date' || key === 'exit_date') {
                        form.value[key] = props.transactionOrderData[key] ? new Date(props.transactionOrderData[key]).toISOString().slice(0, 10) : null;
                    } else {
                        form.value[key] = props.transactionOrderData[key];
                    }
                });
            }
        }
        getQuotesForSelects();
    } else {
        // populate the form fields
        symbol.value = props.transactionItem.symbol;
        form.value.symbol = props.transactionItem.symbol;
        Object.keys(form.value).forEach(key => {
            if (key == 'action') {
                form.value[key] = props.transactionItem[key] === 'Buy' ? 1 : -1;
            } else if (key == 'entry_date' || key == 'exit_date') {
                if (props.transactionItem[key]) {
                    let date = new Date(props.transactionItem[key]).toISOString()
                    form.value[key] = date.split('T')[0];
                } else {
                    form.value[key] = props.transactionItem[key];
                }
                // } else if (key == 'commission') {
                // 	form.value[key] = (props.transactionItem['raw'][key]).replace(/\$/g, '');
            } else {
                form.value[key] = props.transactionItem['raw'][key];
            }
        });
    }
});
</script>

<style lang="scss">
#transactionModalWrapper {
    @apply text-left;
    .modal-container {
        width: 26rem;
        @screen xs {
            width: 100%;
        }
    }

    sup {
        @apply text-red-500;
    }

    #transactionSelectors {
        .selector-wrapper {
            .inline-block {
                width: 100% !important;
            }

            .clContactRadioSelector {
                @apply text-right;
                input[type="radio"] {
                    @apply ml-2;
                }
                .inline-block {
                    width: auto !important;
                }
            }

            select.form-control-fixed {
                @apply mb-2;
                width: 100% !important;
            }
        }
    }
}
</style>
